/**
 * History Search Bar Component
 * Search input with icon for filtering history
 *
 * Follows SOLID principles:
 * - SRP: Separated icon rendering and focus handling
 * - OCP: Extensible via icon prop
 * - ISP: Minimal, focused interface
 * - DIP: Icon abstraction allows different implementations
 */

import React, { useRef, useCallback } from 'react';
import {
  spacing,
  typographyColors,
  typography,
  fontPrimary,
  borders,
  backgroundColors,
  strokeColors,
} from '@/design-system/utils';

// Icon component abstraction (SRP, DIP)
interface SearchIconProps {
  width?: number;
  height?: number;
}

const SearchIcon: React.FC<SearchIconProps> = ({ width = 20, height = 20 }) => {
  return (
    /* eslint-disable-next-line @next/next/no-img-element -- SVG icon */
    <img
      src='/assets/images/icons/search_history.svg'
      alt='Search'
      width={width}
      height={height}
      style={{
        display: 'block',
        objectFit: 'contain',
      }}
    />
  );
};

// Container styles abstraction (SRP)
const containerStyles: React.CSSProperties = {
  position: 'relative',
  width: '100%',
  height: '52px',
  backgroundColor: backgroundColors.primary,
  border: `${borders.width.thin} solid ${strokeColors.light}`,
  borderRadius: borders.radius.lg,
  boxShadow: '4px 4px 16px 0px rgba(224, 134, 1, 0.12)',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
};

const innerContainerStyles: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  paddingLeft: spacing[3],
  paddingRight: spacing[2],
};

interface HistorySearchBarProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  icon?: React.ComponentType<SearchIconProps>;
}

/**
 * Focus handler hook (SRP - separation of concerns)
 */
const useInputFocus = () => {
  const inputRef = useRef<HTMLInputElement>(null);

  const focusInput = useCallback(() => {
    inputRef.current?.focus();
  }, []);

  return { inputRef, focusInput };
};

/**
 * Input styles factory (SRP - styling logic separation)
 */
const getInputStyles = (hasValue: boolean): React.CSSProperties => ({
  flex: 1,
  minWidth: 0,
  border: 'none',
  backgroundColor: 'transparent',
  fontFamily: fontPrimary,
  fontSize: typography.size.sm,
  fontWeight: typography.weight.regular,
  color: hasValue ? typographyColors.primary : typographyColors.placeholder,
  outline: 'none',
  padding: 0,
});

/**
 * Button styles (SRP - reusable styling)
 */
const iconButtonStyles: React.CSSProperties = {
  padding: spacing[2],
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: borders.radius.sm,
  flexShrink: 0,
};

export const HistorySearchBar: React.FC<HistorySearchBarProps> = ({
  value,
  onChange,
  placeholder = 'Search History',
  icon: IconComponent = SearchIcon,
}) => {
  const { inputRef, focusInput } = useInputFocus();

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value);
    },
    [onChange]
  );

  return (
    <div style={containerStyles}>
      <div style={innerContainerStyles}>
        <input
          ref={inputRef}
          type='text'
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          style={getInputStyles(!!value)}
        />
        <button
          type='button'
          style={iconButtonStyles}
          onClick={focusInput}
          aria-label='Focus search input'
        >
          <IconComponent width={20} height={20} />
        </button>
      </div>
    </div>
  );
};
